// FUNCTIONALITY-112 — Store V2 checkout validation (strict UA phone + live filters).

export interface CheckoutFieldErrors {
  name?: string;
  surname?: string;
  phone?: string;
  city?: string;
  warehouse?: string;
  note?: string;
  paymentMethod?: string;
}

const NAME_CHAR_RE = /^[\p{L}'’\- ]+$/u;
const NAME_FULL_RE = /^[\p{L}](?:[\p{L}'’\- ]{0,38}[\p{L}])?$/u;

const PROFANITY = [
  "бляд",
  "хуй",
  "пизд",
  "ёб",
  "ебa",
  "сука",
  "fuck",
  "shit",
  "bitch",
];

const INJECTION = [
  "ignore previous",
  "ignore all",
  "system prompt",
  "developer message",
  "you are chatgpt",
  "act as",
  "jailbreak",
  "drop table",
  "sql ",
  "<script",
  "javascript:",
  "{{",
  "}}",
  "sudo ",
  "rm -rf",
];

const UA_MOBILE_PREFIX =
  /^(39|50|63|66|67|68|73|91|92|93|94|95|96|97|98|99)/;

/** Live filter: letters + apostrophe/hyphen/space only, max 40. */
export function filterPersonNameInput(raw: string): string {
  return String(raw ?? "")
    .replace(/[^\p{L}'’\- ]/gu, "")
    .slice(0, 40);
}

/**
 * Live filter: only + and digits; block spaces/dashes/brackets/letters.
 * Max length +380XXXXXXXXX (13).
 */
export function filterPhoneInput(raw: string): string {
  let v = String(raw ?? "").replace(/[^\d+]/g, "");
  if (v.startsWith("+")) {
    v = `+${v.slice(1).replace(/\+/g, "").replace(/\D/g, "")}`;
  } else {
    v = v.replace(/\+/g, "").replace(/\D/g, "");
  }
  return v.slice(0, 13);
}

/** Live hint while typing — first char must be + or 0. */
export function phoneLiveHint(raw: string): string | null {
  const t = String(raw ?? "").trim();
  if (!t) return null;
  if (t[0] !== "+" && t[0] !== "0") {
    return "Номер має починатися з +380… або 0…";
  }
  return null;
}

/**
 * Strict submit normalization.
 * Allowed raw forms only: +380XXXXXXXXX or 0XXXXXXXXX.
 * Rejects 380… without +, spaces/dashes, letters, invalid operator prefix.
 */
export function normalizeUaPhone(raw: string): string | null {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;
  if (/[\s()\-.]/.test(trimmed)) return null;
  if (/[^\d+]/.test(trimmed)) return null;

  let e164: string | null = null;
  if (/^\+380\d{9}$/.test(trimmed)) e164 = trimmed;
  else if (/^0\d{9}$/.test(trimmed)) e164 = `+380${trimmed.slice(1)}`;
  else return null;

  const local = e164.slice(4);
  if (!UA_MOBILE_PREFIX.test(local)) return null;
  return e164;
}

export function isValidUaPhone(raw: string): boolean {
  return normalizeUaPhone(raw) != null;
}

export function isValidPersonName(raw: string): boolean {
  const v = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (v.length < 2 || v.length > 40) return false;
  if (/\d/.test(v)) return false;
  if (!NAME_CHAR_RE.test(v)) return false;
  return NAME_FULL_RE.test(v);
}

/** NOVA-POSHTA-117B-R7A — reject obvious placeholder / mash names without blocking legitimate UA/Latin. */
const PLACEHOLDER_NAME_RE =
  /^(test|asdf|qwerty|xxx|aaa|bbb|name|surname|ім'?я|прізвище|тест|фів|фыв|llll|aaaa|lklsk|лдла|pending_recipient)$/iu;
const LATIN_VOWELS = /[aeiouyаеєиіїоуюяё]/iu;

export function isPlaceholderPersonName(raw: string): boolean {
  const t = String(raw ?? "").trim();
  if (!t || t.length < 2) return true;
  if (PLACEHOLDER_NAME_RE.test(t)) return true;
  if (/pending_recipient/i.test(t)) return true;
  if (/^[A-Za-z'’\-]+$/.test(t) && !LATIN_VOWELS.test(t)) return true;
  return false;
}

function containsBlocked(text: string): boolean {
  const lower = text.toLowerCase();
  return PROFANITY.some((w) => lower.includes(w)) || INJECTION.some((w) => lower.includes(w));
}

export function sanitizeComment(raw: string, max = 300): { ok: boolean; value: string; error?: string } {
  const trimmed = String(raw ?? "").trim().slice(0, max);
  if (!trimmed) return { ok: true, value: "" };
  if (containsBlocked(trimmed)) {
    return { ok: false, value: "", error: "Коментар містить недопустимий текст." };
  }
  return { ok: true, value: trimmed };
}

export function sanitizePlaceText(
  raw: string,
  fieldLabel: string,
  minLen = 1,
): { ok: boolean; value: string; error?: string } {
  const trimmed = String(raw ?? "").trim().replace(/\s+/g, " ").slice(0, 120);
  if (trimmed.length < minLen) return { ok: false, value: "", error: `Вкажіть ${fieldLabel}.` };
  if (containsBlocked(trimmed)) {
    return { ok: false, value: "", error: `${fieldLabel}: недопустимий текст.` };
  }
  return { ok: true, value: trimmed };
}

/** Canonical machine values for Store V2 checkout (UI → Pages → Worker → ledger). */
export type PaymentMethodChoice = "bank_transfer_fop" | "nova_poshta_cod" | "novapay_card";

export const STORE_V2_PAYMENT_METHODS: readonly PaymentMethodChoice[] = [
  "bank_transfer_fop",
  "nova_poshta_cod",
  "novapay_card",
] as const;

export function isStoreV2PaymentMethod(raw: string | null | undefined): raw is PaymentMethodChoice {
  return STORE_V2_PAYMENT_METHODS.includes(String(raw ?? "").trim() as PaymentMethodChoice);
}

export interface CheckoutInputForValidation {
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  note?: string;
  paymentMethod?: string | null;
}

export function validateCheckout(input: CheckoutInputForValidation): {
  ok: boolean;
  errors: CheckoutFieldErrors;
  normalizedPhone: string | null;
  contactName: string;
  sanitizedNote: string;
  city: string;
  warehouse: string;
} {
  const errors: CheckoutFieldErrors = {};
  const name = input.name.trim().replace(/\s+/g, " ");
  const surname = input.surname.trim().replace(/\s+/g, " ");
  const liveHint = phoneLiveHint(input.phone);
  const normalizedPhone = normalizeUaPhone(input.phone);
  const cityRes = sanitizePlaceText(input.city, "місто", 2);
  const whRes = sanitizePlaceText(input.warehouse, "відділення", 1);
  const noteRes = sanitizeComment(input.note ?? "", 300);

  if (!isValidPersonName(name) || isPlaceholderPersonName(name)) {
    errors.name = "Вкажіть справжнє ім’я та прізвище одержувача.";
  }
  if (!isValidPersonName(surname) || isPlaceholderPersonName(surname)) {
    errors.surname = "Вкажіть справжнє ім’я та прізвище одержувача.";
  }
  if (!input.phone.trim()) errors.phone = "Вкажіть номер телефону.";
  else if (liveHint) errors.phone = liveHint;
  else if (input.phone.includes("*") || /^\*+\d{0,4}$/.test(input.phone.trim())) {
    errors.phone = "Вкажіть повний номер телефону.";
  } else if (!normalizedPhone) {
    errors.phone = "Введіть номер у форматі +380XXXXXXXXX або 0XXXXXXXXX";
  }
  if (!cityRes.ok && cityRes.error) errors.city = cityRes.error;
  if (!whRes.ok && whRes.error) errors.warehouse = whRes.error;
  if (!noteRes.ok && noteRes.error) errors.note = noteRes.error;

  const pm = String(input.paymentMethod ?? "").trim();
  if (!pm) errors.paymentMethod = "Оберіть спосіб оплати.";
  else if (!isStoreV2PaymentMethod(pm)) {
    errors.paymentMethod = "Оберіть доступний спосіб оплати.";
  }

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    normalizedPhone,
    contactName: `${name} ${surname}`.trim(),
    sanitizedNote: noteRes.value,
    city: cityRes.value,
    warehouse: whRes.value,
  };
}
